import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing meta tags
 * 
 * Renders route-specific metadata that matches the prerendered HTML.
 * Uses react-helmet-async for dynamic updates after hydration.
 * 
 * IMPORTANT: Canonical URLs use trailing slashes consistently.
 */

const SITE_URL = 'https://allsecured.com.au';
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_0e5aee2f-27de-4cc5-bc22-1ba27f01672b/artifacts/f8dzsnko_Variant%201_Shield%20Only_Vector.png';

// Route-specific SEO configuration (must match prerender.js exactly)
const SEO_CONFIG = {
  // Main pages
  '/': {
    title: 'Digital Safety & Cyber Privacy Courses | All Secured',
    description: 'Practical digital safety and cyber privacy education for everyday people. Stop silent tracking, reduce profiling, prevent scams, and protect your digital life step by step.',
    ogType: 'website'
  },
  '/courses': {
    title: 'Online Privacy & Security Courses | Digital Safety Training | All Secured',
    description: 'Explore practical online courses covering cyber privacy basics, scam prevention, encrypted email, and tracking protection. Simple, clear, actionable digital safety training.',
    ogType: 'website'
  },
  '/courses/cyber-privacy-security-basics': {
    title: 'Cyber Privacy & Security Basics Course | Limit Tracking and Profiling & Protect Your Digital Life',
    description: 'A practical online course designed to help you reduce silent tracking and profiling, prevent scams, understand safety tools like VPN and reset your digital security. No jargon. Step by step.',
    ogType: 'product'
  },
  
  // Homepage section routes
  '/first-step': {
    title: 'First Step: Start Your Digital Safety Reset | All Secured',
    description: 'Start tightening your privacy and security with guided steps. Reduce tracking exposure and fix weak settings before problems escalate.',
    ogType: 'website'
  },
  '/baseline': {
    title: 'Simple Cybersecurity Training (No Jargon) | All Secured',
    description: 'Clear, guided steps for all levels of digital literacy. Apply real protections as you go, verify with checkpoints, and turn safer settings into second nature.',
    ogType: 'website'
  },
  '/your-stack': {
    title: 'Secure Your Devices & Accounts | Quick Cyber Safety Tour | All Secured',
    description: 'Take a quick tour of your digital life—phone, laptop, router, cloud, email, smart devices and more. See what to tighten, why it matters, and how to lock it in with guided steps.',
    ogType: 'website'
  },
  '/tools': {
    title: 'Recommended Privacy & Security Tools | Stop Online Tracking',
    description: 'Discover carefully selected privacy tools including secure browsers, search engines, encrypted email providers, and scam protection resources.',
    ogType: 'website'
  },
  '/testimonials': {
    title: 'All Secured Reviews | Real Results From Real People',
    description: 'See how everyday people improved their privacy and security with All Secured. Real outcomes, real mindset shifts, and step-by-step changes that actually stick.',
    ogType: 'website'
  },
  '/safety-check': {
    title: 'Digital Safety Check | Quick Online Security Self-Assessment | All Secured',
    description: 'A fast online security check that reveals common blind spots—password reuse, weak recovery setup, risky defaults and more. Get your snapshot, then follow guided fixes.',
    ogType: 'website'
  },
  '/social': {
    title: 'Follow All Secured | Digital Safety on Social Media',
    description: 'Connect with All Secured on social media for digital safety tips, privacy updates, and cybersecurity news. Stay informed and protected.',
    ogType: 'website'
  },
  '/contact': {
    title: 'Contact All Secured | Course Guidance & Support',
    description: 'Not sure which course to start with? Contact All Secured for help choosing the right course, navigating the site, or understanding what to do next—simple and jargon-free.',
    ogType: 'website'
  },
  
  // Legal pages
  '/privacy-policy': {
    title: 'Privacy Policy | All Secured',
    description: 'Read how All Secured collects, uses, stores, and protects personal information, including tracking, analytics, and customer support communications.',
    ogType: 'website'
  },
  '/terms-of-use': {
    title: 'Terms of Use | All Secured',
    description: 'The terms and conditions for using the All Secured website, tools, and course content, including acceptable use and limitations.',
    ogType: 'website'
  },
  '/refund-policy': {
    title: 'Refund Policy | All Secured',
    description: 'Understand eligibility, timeframes, and how to request refunds for All Secured courses and digital products.',
    ogType: 'website'
  },
  '/cookie-policy': {
    title: 'Cookie Policy | All Secured',
    description: 'Learn what cookies and similar technologies are used on All Secured, why they are used, and how to manage your preferences.',
    ogType: 'website'
  },
  '/security-statement': {
    title: 'Security Statement | All Secured',
    description: 'How All Secured approaches security, data handling, and platform protections, plus how to report security concerns responsibly.',
    ogType: 'website'
  },

  // Perth Property Pulse
  '/perth-property-pulse': {
    title: 'Perth Property Pulse | Perth Property Intelligence for Homeowners & Buyers',
    description: 'Perth Property Pulse is a premium Perth property intelligence hub for homeowners, buyers and sellers, covering market trends, council planning, climate, gardens, home care and suburb insights.',
    ogType: 'website'
  },
  
  // Auth pages (noindex)
  '/login': {
    title: 'Login | All Secured',
    description: 'Sign in to your All Secured account to access your courses and dashboard.',
    ogType: 'website',
    noIndex: true
  },
  '/signup': {
    title: 'Sign Up | All Secured',
    description: 'Create your All Secured account to get started with digital safety courses.',
    ogType: 'website',
    noIndex: true
  },
  
  // Blueprint landing page (hidden, noindex)
  '/course-1-blueprint': {
    title: 'Course 1 PDF Blueprint | All Secured',
    description: 'A calm, step-by-step PDF Blueprint that turns security into a simple checklist you can follow at your own pace.',
    ogType: 'product',
    noIndex: true
  },
  '/course-1-blueprint/thank-you': {
    title: 'Thank You | All Secured',
    description: 'Your purchase is complete. Check your email for your Blueprint download.',
    ogType: 'website',
    noIndex: true
  }
};

const SEO = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType,
  twitterCard = 'summary_large_image',
  noIndex = false,
  structuredData = null,
  children
}) => {
  // Get config from route if not explicitly provided
  const routeConfig = canonical ? SEO_CONFIG[canonical] : null;
  
  const finalTitle = title || routeConfig?.title || SEO_CONFIG['/'].title;
  const finalDescription = description || routeConfig?.description || SEO_CONFIG['/'].description;
  const finalOgType = ogType || routeConfig?.ogType || 'website';
  const finalNoIndex = noIndex || routeConfig?.noIndex || false;
  
  // Canonical URL with trailing slash
  const canonicalUrl = canonical 
    ? (canonical === '/' ? `${SITE_URL}/` : `${SITE_URL}${canonical}/`)
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalNoIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={finalOgType} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="All Secured" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

// Export the config for use in other components
export { SEO_CONFIG, SITE_URL, DEFAULT_OG_IMAGE };
export default SEO;
